import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, CircleDollarSign, FileText, Camera, ThumbsUp, ThumbsDown, AlertTriangle,
  CheckCircle2, XCircle, BadgeCheck,
} from "lucide-react";
import { Wizard, WField, WNote } from "@/components/fs/Wizard";
import { V } from "@/lib/vocab";

const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");
const PILL = {
  submitted: ["bg-amber-50 text-amber-800", "To review"],
  board_review: ["bg-indigo-50 text-indigo-700", "With the board"],
  approved: ["bg-emerald-50 text-emerald-700", "Approved"],
  exported: ["bg-emerald-50 text-emerald-700", "Sent to accounting"],
  paid: ["bg-sand-200 text-ink/60", "Paid"],
  rejected: ["bg-red-50 text-red-700", "Not approved"],
};
const Metric = ({ label, value, tone = "" }) => (
  <div className={`rounded-xl border border-sand-200 px-3 py-2 ${tone}`}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-ink/40">{label}</div>
    <div className="text-sm font-bold text-ink">{value}</div>
  </div>
);

/* Wizard 5 — approve an invoice. Four questions in the order that matters: is this the
   document, does the maths work, is there proof it happened, and where does it land. */
export default function ApproveInvoiceWizard({ tenantId, iid, readOnly, canDecide, onChanged, onClose }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tenants/${tenantId}/field-services/invoices/${iid}`);
    setD(data); setCode(data.invoice.gl_code || "");
  }, [tenantId, iid]);
  useEffect(() => { load().catch(() => toast.error("Couldn't open that invoice")); }, [load]);

  if (!d) return <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;
  const iv = d.invoice; const p = d.proof || {};
  const openState = ["submitted", "board_review"].includes(iv.status);
  const blockers = d.blockers || [];

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };
  const approve = (vouch) => act(async () => {
    const { data } = await api.post(`/tenants/${tenantId}/field-services/invoices/${iid}/approve`, {
      vouch, gl_code: code || undefined, signed_by: signedBy || undefined,
    });
    if (data.needs_board) toast.info(`That's at or above ${usd(d.threshold)} — sent to the ${V.grp()} to sign off`);
    else toast.success("Approved");
  });

  const header = (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${(PILL[iv.status] || [])[0]}`}>
        {(PILL[iv.status] || [])[1] || iv.status}
      </span>
      <span className="text-sm font-bold text-ink">{iv.vendor_name}</span>
      <span className="text-xs text-ink/45">{iv.wo_ref} · {iv.type}{iv.number ? ` · #${iv.number}` : ""}</span>
      <span className="ml-auto text-lg font-extrabold text-ink">{usd(iv.amount)}</span>
    </div>
  );

  /* Settled invoices don't need a wizard — they need the receipt. */
  if (!openState || readOnly) {
    return (
      <div className="space-y-4" data-testid={`invoice-review-${iid}`}>
        {header}
        <div className="grid gap-2 sm:grid-cols-3">
          <Metric label="Agreed" value={usd(p.awarded)} />
          <Metric label="Committed" value={usd(p.committed)} />
          <Metric label="This invoice" value={usd(p.this_invoice)} />
        </div>
        {iv.file_url && (
          <a href={iv.file_url} target="_blank" rel="noreferrer" data-testid="invoice-file"
            className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/70">
            <FileText className="h-4 w-4" /> Open the invoice PDF
          </a>
        )}
        {["approved", "exported"].includes(iv.status) && !readOnly && (
          <div className="rounded-xl border border-sand-200 bg-white px-4 py-3">
            <div className="text-sm font-bold text-ink">Payment happens in your accounting system.</div>
            <p className="mt-0.5 text-xs text-ink/55">
              Redline never moves money. Once it's paid, mark it here and we'll tell the vendor.
            </p>
            <button data-testid="invoice-mark-paid" disabled={busy} onClick={() => {
              const reference = window.prompt("Cheque or payment reference (optional)") ?? "";
              act(() => api.post(`/tenants/${tenantId}/field-services/invoices/${iid}/mark-paid`, { reference }),
                "Marked paid — the vendor was told");
            }} className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
              <CircleDollarSign className="h-4 w-4" /> Mark it paid
            </button>
          </div>
        )}
        {iv.status === "rejected" && (
          <div className="rounded-xl bg-red-50 px-4 py-3 text-xs text-red-900">Sent back: {iv.reject_reason}</div>
        )}
        {iv.status === "paid" && (
          <div className="rounded-xl bg-sand-100 px-4 py-3 text-xs text-ink/60">
            Paid {when(iv.paid_at)}{iv.paid_ref ? ` · ref ${iv.paid_ref}` : ""}
          </div>
        )}
        {iv.manager_vouched && (
          <div className="rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-900">
            A manager vouched for this one past the automatic checks.
          </div>
        )}
        {readOnly && openState && (
          <WNote testid="invoice-readonly">{V.many("board")} can see everything here; approving is the manager's call unless it's over the threshold.</WNote>
        )}
      </div>
    );
  }

  const steps = [
    {
      key: "doc", title: "The document",
      hint: "Start with what they actually sent. If the number they typed and the number on the page disagree, that's worth thirty seconds now.",
      render: () => (
        <>
          {header}
          {iv.amount_mismatch && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900"
              data-testid="invoice-mismatch">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>They typed {usd(iv.amount_mismatch.typed)} but the PDF reads {usd(iv.amount_mismatch.read)}.
                We kept what they typed — check the document before approving.</span>
            </div>
          )}
          {iv.file_url ? (
            <a href={iv.file_url} target="_blank" rel="noreferrer" data-testid="invoice-file"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2.5 text-sm font-bold text-ink/70 hover:border-ink/30">
              <FileText className="h-4 w-4" /> Open the invoice PDF
            </a>
          ) : (
            <WNote tone="amber" testid="invoice-no-file">No PDF attached — they submitted an amount only.</WNote>
          )}
          {iv.note && <div className="rounded-xl bg-sand-50 px-4 py-3 text-sm text-ink/70">{iv.note}</div>}
        </>
      ),
    },
    {
      key: "maths", title: "Does the maths work",
      hint: "What was agreed, what was added since, what's already been approved, and what's left.",
      render: () => (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            <Metric label="Agreed" value={usd(p.awarded)} />
            <Metric label={`Change orders (${p.change_orders || 0})`} value={usd(p.change_order_total)} />
            <Metric label="Committed" value={usd(p.committed)} />
            <Metric label="Already approved" value={usd(p.billed_before)} />
            <Metric label="This invoice" value={usd(p.this_invoice)} />
            <Metric label="Variance" value={p.variance === null ? "—" : usd(p.variance)}
              tone={p.over_committed ? "bg-red-50" : ""} />
          </div>
          {p.over_committed && (
            <WNote tone="red" testid="invoice-over">
              This takes the total past what was committed. Either there's a change order missing, or
              they're billing for something nobody agreed to.
            </WNote>
          )}
        </>
      ),
    },
    {
      key: "proof", title: "Is there proof",
      hint: "The reason an invoice is trustworthy isn't the invoice — it's the record of the work.",
      render: () => (
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 ${p.completed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> {p.completed ? `Complete ${when(p.completed_at)}` : "Work not marked complete"}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 ${p.photos ? "bg-emerald-50 text-emerald-700" : "bg-sand-100 text-ink/50"}`}>
            <Camera className="h-3.5 w-3.5" /> {p.photos || 0} photo(s)
          </span>
          {(p.resident_thumbs?.up || 0) > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">
              <ThumbsUp className="h-3.5 w-3.5" /> {V.lc("person")} happy
            </span>
          )}
          {(p.resident_thumbs?.down || 0) > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1.5 text-red-700">
              <ThumbsDown className="h-3.5 w-3.5" /> {V.lc("person")} wasn't happy
            </span>
          )}
          {p.open_punch > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5 text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" /> {p.open_punch} punch item(s) open
            </span>
          )}
        </div>
      ),
    },
    {
      key: "code", title: "Charge it to",
      hint: "Inherited from the job, so it's usually already right. Change it only if the job itself was coded wrong.",
      render: () => (
        <WField label="Accounting code"
          hint={`Inherited from the work order (${iv.gl_code || "not coded"}).`}>
          <select data-testid="invoice-code" value={code} onChange={(e) => setCode(e.target.value)}
            className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="">— inherited from the work order —</option>
            {(d.codes || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
          </select>
        </WField>
      ),
    },
    {
      key: "decide", title: "Decision",
      hint: "Approving hands it to accounting on your export batch. Sending it back tells the vendor exactly what you typed.",
      render: () => (
        <>
          {blockers.length > 0 && (
            <div data-testid="invoice-blockers" className="space-y-2">
              {blockers.map((b) => (
                <div key={b.key} className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs leading-relaxed text-red-900">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" /> {b.message}
                </div>
              ))}
            </div>
          )}

          {d.needs_board && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
              <div className="text-xs font-bold text-indigo-900">
                {usd(iv.amount)} is at or above this {V.lc("org")}'s {usd(d.threshold)} threshold — {V.a("board")} has to sign it off.
              </div>
              {canDecide ? (
                <input data-testid="invoice-signed-by" value={signedBy} onChange={(e) => setSignedBy(e.target.value)}
                  placeholder="Type your full name to sign"
                  className="mt-2 w-full rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm outline-none" />
              ) : (
                <p className="mt-1 text-xs font-semibold text-indigo-900" data-testid="invoice-board-waiting">
                  They've been emailed. Approving here sends it to them rather than to accounting.
                </p>
              )}
            </div>
          )}

          {!rejecting ? (
            <div className="flex flex-wrap gap-2">
              <button data-testid="invoice-approve" disabled={busy || blockers.length > 0} onClick={() => approve(false)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40">
                <BadgeCheck className="h-4 w-4" /> Approve
              </button>
              {blockers.length > 0 && (
                <button data-testid="invoice-vouch" disabled={busy} onClick={() => approve(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-bold text-amber-900 disabled:opacity-40">
                  Approve anyway — I vouch for it
                </button>
              )}
              <button data-testid="invoice-reject" disabled={busy} onClick={() => setRejecting(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2.5 text-sm font-bold text-ink/60 disabled:opacity-40">
                <XCircle className="h-4 w-4" /> Send it back
              </button>
            </div>
          ) : (
            <div className="space-y-2 rounded-xl border border-red-200 bg-red-50/60 p-3">
              <textarea data-testid="invoice-reject-reason" rows={3} value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why isn't this approved? They'll see exactly this."
                className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm outline-none" />
              <div className="flex gap-2">
                <button data-testid="invoice-reject-confirm" disabled={busy || reason.trim().length < 3}
                  onClick={() => act(() => api.post(`/tenants/${tenantId}/field-services/invoices/${iid}/reject`,
                    { reason }), "Sent back to them")}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
                  Send it back
                </button>
                <button onClick={() => setRejecting(false)}
                  className="rounded-xl border border-sand-300 px-3 py-2 text-sm font-semibold text-ink/60">Cancel</button>
              </div>
            </div>
          )}
          {blockers.length > 0 && (
            <WNote tone="amber" testid="invoice-blocked-note">
              Approve is off because of the checks above. If you know it's right anyway, vouch for it — your
              name goes on the record next to the reason.
            </WNote>
          )}
        </>
      ),
    },
  ];

  return (
    <div data-testid={`invoice-review-${iid}`}>
      <Wizard testid="wiz-invoice" steps={steps} busy={busy} finishLabel="Done"
        finishIcon={CheckCircle2} onFinish={() => onClose?.()} />
    </div>
  );
}
